import React from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'ts-key-enum';

import './index.less';

import { AgentMessageEventOutput, AgentMessageEventToolCallStatus, AgentMessageEventType } from '../../../../gamma-api';
import IbIcon, { IbIconName } from '../../common/IbIcon';
import IbSpin from '../../common/IbSpin';
import IbTypography from '../../common/IbTypography';
import { getToolCallEventIconName, getToolCallEventName } from '../ToolCallEvent/utils';
import {
  getGuardrailCheckTypeTitle,
  GuardrailCheckData,
  GuardrailEventData,
  isGuardrailCheckBlocked,
  isGuardrailCheckWarning,
} from '../guardrailChecks';
import {
  buildGuardrailFlow,
  GuardrailDetailsSection,
  GuardrailDetailsTarget,
  GuardrailFlowItemType,
  GuardrailLlmGroup,
} from '../guardrailEventGroups';

const MAIN_CLASS_NAME = 'guardrail-events-timeline';
const ENDPOINT_CLASS_NAME = `${MAIN_CLASS_NAME}__endpoint`;
const ENDPOINT_ERROR_CLASS_NAME = `${ENDPOINT_CLASS_NAME}_error`;
const GROUP_CLASS_NAME = `${MAIN_CLASS_NAME}__group`;
const ROW_CLASS_NAME = `${MAIN_CLASS_NAME}__row`;
const LLM_ROW_CLASS_NAME = `${ROW_CLASS_NAME}_llm-call`;
const CHECKS_CLASS_NAME = `${MAIN_CLASS_NAME}__checks`;
const CHECKS_SUCCESS_CLASS_NAME = `${CHECKS_CLASS_NAME}_success`;
const CHECKS_ERROR_CLASS_NAME = `${CHECKS_CLASS_NAME}_error`;
const CHECKS_WARNING_CLASS_NAME = `${CHECKS_CLASS_NAME}_warning`;
const CHECKS_LIST_CLASS_NAME = `${CHECKS_CLASS_NAME}__list`;
const CHECK_CLASS_NAME = `${MAIN_CLASS_NAME}__check`;
const CHECK_TYPE_CLASS_NAME = `${CHECK_CLASS_NAME}__type`;
const LLM_MODEL_CLASS_NAME = `${ROW_CLASS_NAME}__llm`;
const STATUS_CLASS_NAME = `${MAIN_CLASS_NAME}__status`;
const STATUS_ERROR_CLASS_NAME = `${STATUS_CLASS_NAME}_error`;
const STATUS_WARNING_CLASS_NAME = `${STATUS_CLASS_NAME}_warning`;
const TOOL_CLASS_NAME = `${MAIN_CLASS_NAME}__tool`;
const CLICKABLE_CLASS_NAME = `${MAIN_CLASS_NAME}__clickable`;

interface IGuardrailEventsTimelineProps {
  events: AgentMessageEventOutput[];
  responsePending?: boolean;
  onDetailsSelect?: (target: GuardrailDetailsTarget) => void;
}

const getCheckStatusClass = (check: GuardrailCheckData): string => {
  if (isGuardrailCheckBlocked(check)) {
    return `${STATUS_CLASS_NAME} ${STATUS_ERROR_CLASS_NAME}`;
  }
  if (isGuardrailCheckWarning(check)) {
    return `${STATUS_CLASS_NAME} ${STATUS_WARNING_CLASS_NAME}`;
  }
  return STATUS_CLASS_NAME;
};

const getCheckStatusIcon = (check: GuardrailCheckData, circled: boolean): IbIconName => {
  if (isGuardrailCheckBlocked(check)) {
    return circled ? 'close-one' : 'close-small';
  }
  if (isGuardrailCheckWarning(check)) {
    return 'attention';
  }
  return circled ? 'check-one' : 'check-small';
};

const GuardrailEventsTimeline: React.FC<IGuardrailEventsTimelineProps> = ({
  events,
  responsePending,
  onDetailsSelect,
}) => {
  const { t } = useTranslation();
  const flow = buildGuardrailFlow(events);
  const getLlmName = (group: GuardrailLlmGroup) => (group.llmCall ? group.llmCall.model_name : '');
  const getNavigationProps = (target: GuardrailDetailsTarget): React.HTMLAttributes<HTMLDivElement> => {
    if (!onDetailsSelect) {
      return {};
    }
    const selectTarget = () => onDetailsSelect?.(target);
    return {
      role: 'button',
      tabIndex: 0,
      onClick: selectTarget,
      onKeyDown: (event) => {
        if (event.key === Key.Enter || event.key === ' ') {
          event.preventDefault();
          selectTarget();
        }
      },
    };
  };
  const getClickableClassName = (className: string, clickable = true): string =>
    [className, clickable && onDetailsSelect ? CLICKABLE_CLASS_NAME : ''].filter(Boolean).join(' ');

  const renderStatus = (checks: GuardrailCheckData[], circled = false) => {
    const blockedCheck = checks.find(isGuardrailCheckBlocked);
    const warningCheck = checks.find(isGuardrailCheckWarning);
    const check = blockedCheck || warningCheck || checks[0];
    if (!check) {
      return null;
    }

    return <IbIcon className={getCheckStatusClass(check)} iconName={getCheckStatusIcon(check, circled)} size={16} />;
  };

  const renderCheckGroup = (checks: GuardrailCheckData[], input: boolean, groupKey: string) => {
    if (!checks.length) {
      return null;
    }
    const section = input ? GuardrailDetailsSection.Input : GuardrailDetailsSection.Output;
    const classes = [CHECKS_CLASS_NAME];
    if (checks.some(isGuardrailCheckBlocked)) {
      classes.push(CHECKS_ERROR_CLASS_NAME);
    } else if (checks.some(isGuardrailCheckWarning)) {
      classes.push(CHECKS_WARNING_CLASS_NAME);
    } else {
      classes.push(CHECKS_SUCCESS_CLASS_NAME);
    }
    return (
      <div className={classes.join(' ')}>
        <div className={getClickableClassName(ROW_CLASS_NAME)} {...getNavigationProps({ groupKey, section })}>
          <IbIcon iconName={input ? 'guardrail-input' : 'guardrail-output'} size={16} />
          <IbTypography.Paragraph type="secondary">
            {t(`Agent: Guardrail checks timeline: ${input ? 'Input checks' : 'Output checks'}`)}
          </IbTypography.Paragraph>
          {renderStatus(checks, true)}
        </div>
        <div className={CHECKS_LIST_CLASS_NAME}>
          {checks.map((check, index) => (
            <div
              key={`${check.check_id}:${index}`}
              className={getClickableClassName(CHECK_CLASS_NAME)}
              {...getNavigationProps({ groupKey, section, checkIndex: index })}
            >
              <IbTypography.Paragraph type="secondary">
                {check.check_name?.trim() || t('Agent: Guardrail checks timeline: Unnamed check')}{' '}
                <span className={CHECK_TYPE_CLASS_NAME}>({getGuardrailCheckTypeTitle(check)})</span>
              </IbTypography.Paragraph>
              {renderStatus([check])}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLlmGroup = (group: GuardrailLlmGroup) => {
    const checks = [...group.inputChecks, ...group.outputChecks];
    const hasDetails = Boolean(group.llmCall || checks.length);

    return (
      <div key={group.key} className={GROUP_CLASS_NAME}>
        {renderCheckGroup(group.inputChecks, true, group.key)}
        {group.llmCall && (
          <div
            className={getClickableClassName([ROW_CLASS_NAME, LLM_ROW_CLASS_NAME].join(' '), hasDetails)}
            {...(hasDetails ? getNavigationProps({ groupKey: group.key, section: GuardrailDetailsSection.Llm }) : {})}
          >
            <IbIcon iconName="robot-one" size={16} />
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Guardrail checks timeline: Agent LLM call')}
              <span className={LLM_MODEL_CLASS_NAME}>{getLlmName(group) ? ` (${getLlmName(group)})` : ''}</span>
            </IbTypography.Paragraph>
          </div>
        )}
        {renderCheckGroup(group.outputChecks, false, group.key)}
      </div>
    );
  };

  const renderEndpoint = (iconName: IbIconName, title: string, error = false, showStatus = false) => (
    <div className={[ENDPOINT_CLASS_NAME, error ? ENDPOINT_ERROR_CLASS_NAME : ''].filter(Boolean).join(' ')}>
      <IbIcon iconName={iconName} size={16} />
      <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
      {showStatus && (
        <IbIcon
          className={[STATUS_CLASS_NAME, error ? STATUS_ERROR_CLASS_NAME : ''].filter(Boolean).join(' ')}
          iconName={error ? 'close-small' : 'check-small'}
          size={16}
        />
      )}
    </div>
  );

  const checks = events
    .filter((event) => event.data.event_type === AgentMessageEventType.Guardrail)
    .flatMap((event) => (event.data as GuardrailEventData).checks || []);
  const responseBlocked = checks.some(isGuardrailCheckBlocked);
  const responseFailed = events.some((event) => event.data.event_type === AgentMessageEventType.RuntimeError);
  const responseFinished = responseBlocked || responseFailed || !responsePending;

  return (
    <section className={MAIN_CLASS_NAME}>
      {renderEndpoint('me', t('Agent: Guardrail checks timeline: User message'))}
      {flow.map((flowItem) => {
        if (flowItem.type === GuardrailFlowItemType.Llm) {
          return renderLlmGroup(flowItem.group);
        }

        const { toolCall } = flowItem.item;
        const isToolCallError = toolCall.status === AgentMessageEventToolCallStatus.Error;
        return (
          <div key={flowItem.item.key} className={TOOL_CLASS_NAME}>
            <IbIcon iconName={getToolCallEventIconName(toolCall)} size={16} />
            <IbTypography.Paragraph type="secondary">{getToolCallEventName(toolCall)}</IbTypography.Paragraph>
            {toolCall.status === AgentMessageEventToolCallStatus.Processing ? (
              <IbSpin size="small" />
            ) : (
              <IbIcon
                className={[STATUS_CLASS_NAME, ...(isToolCallError ? [STATUS_ERROR_CLASS_NAME] : [])].join(' ')}
                iconName={isToolCallError ? 'close-small' : 'check-small'}
                size={16}
              />
            )}
          </div>
        );
      })}
      {responseFinished &&
        renderEndpoint(
          'robot-one',
          t(
            responseBlocked
              ? 'Agent: Guardrail checks timeline: Response blocked'
              : responseFailed
              ? 'Agent: Guardrail checks timeline: Agent error'
              : 'Agent: Guardrail checks timeline: Agent response'
          ),
          responseBlocked || responseFailed,
          true
        )}
    </section>
  );
};

export default GuardrailEventsTimeline;
